import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { conferenceService } from '../services/conferenceService';

/**
 * Which conference am I in, and what am I allowed to do *here*?
 *
 * Roles in this product are per conference, not per profile: the same person
 * can chair one conference, review for a second and author a paper in a third.
 * The old navigation ignored that entirely — it showed a fixed set of links
 * based on the account, so a chair saw "Reviews" they'd never use and a
 * reviewer saw no way to reach the conference they were reviewing for.
 *
 * This context reads `?conferenceId=` from the URL (which is how every
 * conference-scoped route already identifies itself) and answers two questions
 * for the chrome above the page: which conference, and which roles do I hold in
 * it. Everything the navigation offers follows from those two answers.
 *
 * Failure is deliberately quiet. Navigation is not allowed to be the thing that
 * takes a page down, so every error path resolves to "no context" and the bar
 * simply doesn't render.
 */

const ConferenceContext = createContext({
  conferenceId: null,
  conference: null,
  roles: [],
  myConferences: [],
  loading: false,
  canManage: false,
  isReviewer: false,
  isAuthor: false,
});

export function ConferenceProvider({ children }) {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  const conferenceId = useMemo(() => {
    const params = new URLSearchParams(location?.search || '');
    return params.get('conferenceId') || params.get('id') || null;
  }, [location?.search]);

  const [conference, setConference] = useState(null);
  const [roles, setRoles] = useState([]);
  const [myConferences, setMyConferences] = useState([]);
  const [loading, setLoading] = useState(false);

  // The switcher's list changes rarely, so it's fetched once per session rather
  // than on every navigation.
  useEffect(() => {
    let cancelled = false;
    if (!isAuthenticated || !user?.id) {
      setMyConferences([]);
      return undefined;
    }
    conferenceService
      ?.getMyConferences(user?.id)
      ?.then((list) => {
        if (!cancelled) setMyConferences(list || []);
      })
      ?.catch(() => {
        if (!cancelled) setMyConferences([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    let cancelled = false;

    if (!conferenceId || !isAuthenticated || !user?.id) {
      setConference(null);
      setRoles([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    Promise.all([
      conferenceService?.getById(conferenceId)?.catch(() => null),
      conferenceService?.getMyRolesForConference(user?.id, conferenceId)?.catch(() => []),
    ])
      .then(([conf, myRoles]) => {
        if (cancelled) return;
        setConference(conf || null);
        setRoles(myRoles || []);
        // The email-template screen reads its conference from localStorage
        // rather than the URL. Mirroring the active id keeps that screen
        // reachable from the conference bar instead of only from whatever
        // happened to set the key earlier in the session.
        try {
          if (conf?.id) window.localStorage?.setItem('selectedConferenceId', String(conf.id));
        } catch {
          /* storage unavailable (private mode, blocked cookies) — not fatal */
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [conferenceId, isAuthenticated, user?.id]);

  const value = useMemo(() => {
    const has = (r) => roles?.includes(r);
    return {
      conferenceId,
      conference,
      roles,
      myConferences,
      loading,
      // Chair and organiser both manage; the difference between them is
      // permission depth inside a screen, not which screens exist.
      canManage: has('chair') || has('organizer'),
      isReviewer: has('reviewer'),
      isAuthor: has('author'),
    };
  }, [conferenceId, conference, roles, myConferences, loading]);

  return <ConferenceContext.Provider value={value}>{children}</ConferenceContext.Provider>;
}

export function useConferenceContext() {
  return useContext(ConferenceContext);
}

export default ConferenceContext;
